import axios from "axios";

/**
 * Service du chatbot IA : appelle le proxy Laravel, qui appelle l'API Claude.
 * La clé API reste côté serveur ; le JWT est ajouté par l'intercepteur global d'App.jsx.
 *
 * Forme d'une réponse : { blocks: [ ... ], error?: true } ou { pending: { id, tool, text, query, reason } } où :
 *   - pending : Claude demande un outil traité par le navigateur ; renvoyer le résultat avec
 *     sendChatbotToolResult(pending.id, ...) pour obtenir la suite :
 *       - lire_page : { content: texte de la page } (automatique) ;
 *       - surligner : { content: compte rendu } (automatique, voir highlightOnPage) ;
 *       - demander_recherche_web : { approved: true|false } (clic de l'utilisateur sur la requête proposée) ;
 *       - modifier_article ou exporter_articles : { approved: true|false } (clic sur « Confirmer » ou
 *         « Annuler » du récapitulatif ; exporter_articles ne rend jamais le fichier dans le chat, il
 *         apparaît dans le menu « Exports CSV » une fois prêt) ;
 *       - generer_graphique_vente : { content: JSON.stringify(pending.chart) } (automatique, le contenu
 *         du graphique déjà calculé par le serveur, rendu dans une modale et simplement renvoyé tel quel).
 *   - chaque bloc est l'un de :
 *     - { type: 'text',    text | key }                texte libre (text) ou clé de traduction (key)
 *     - { type: 'info',    key }                       mention discrète (ex. page consultée), hors historique
 *     - { type: 'sources', items: [{ url, title }] }   sources web citées (affichage exigé par Anthropic), hors historique
 *     - { type: 'link',    labelKey, route }           bouton de navigation vers une page interne
 *     - { type: 'confirm', summaryKey, confirmedKey }  demande de confirmation avant écriture
 *     - { type: 'write_approval', pendingId, write, tool } récapitulatif d'une modification ou d'un export,
 *       à confirmer ou annuler ; tool distingue le rendu (modifier_article ou exporter_articles)
 *     - { type: 'validation', items: [{ article, champ, keys }] } erreurs de saisie d'une écriture refusée, traduites ici
 *     - { type: 'file', nom, contenu, lignes }           fichier produit par un outil (CSV d'import), à télécharger
 *     - { type: 'memory', action, texte }                consigne mémorisée, remplacée ou oubliée par l'assistant, hors historique
 * (link servira avec l'ouverture automatique des pages.)
 */

/** Outil de lecture de la page ouverte, exécuté par le navigateur (même nom que ChatbotGestion::TOOL_READ_PAGE). */
export const TOOL_READ_PAGE = "lire_page";

/** Outil d'interface : entoure en rouge un élément de la page (même nom que ChatbotGestion::TOOL_HIGHLIGHT). */
export const TOOL_HIGHLIGHT = "surligner";

// Classe CSS posée sur l'élément entouré (définie dans perso.css)
const HIGHLIGHT_CLASS = "chatbot-ia-highlight";

/** Demande d'autorisation de recherche web, décidée par l'utilisateur (même nom que ChatbotGestion::TOOL_ASK_WEB). */
export const TOOL_ASK_WEB = "demander_recherche_web";

/**
 * Graphique de vente B2C (même nom que ChatbotGestion::TOOL_GENERATE_CHART) : le calcul est déjà fait
 * côté serveur (jamais recalculé ici), le front se contente d'afficher `pending.chart` dans une modale
 * et de renvoyer ce même contenu tel quel pour reprendre la conversation (comme lire_page/surligner,
 * automatique, sans action de l'utilisateur à attendre).
 */
export const TOOL_GENERATE_CHART = "generer_graphique_vente";

/**
 * Modification d'un article, soumise à confirmation (même nom que ChatbotEcritureGestion::TOOL_ARTICLE_UPDATE).
 * Le backend ne fait que préparer le récapitulatif : rien n'est enregistré tant que l'utilisateur n'a pas cliqué.
 */
export const TOOL_ARTICLE_UPDATE = "modifier_article";

/**
 * Export CSV de la base article, soumis à confirmation (même nom que ChatbotEcritureGestion::TOOL_ARTICLE_EXPORT).
 * Le backend ne lance rien tant que l'utilisateur n'a pas cliqué ; une fois lancé, le fichier n'est
 * jamais donné dans le chat, il apparaît dans le menu « Exports CSV » comme n'importe quel export du site.
 */
export const TOOL_ARTICLE_EXPORT = "exporter_articles";

/**
 * Gestion du catalogue des familles (créer, renommer, archiver une valeur de classification),
 * soumise à confirmation (même nom que ChatbotEcritureGestion::TOOL_FAMILLE_WRITE).
 * Comme pour la modification d'un article, le backend n'écrit rien avant le clic de l'utilisateur.
 */
export const TOOL_FAMILLE_WRITE = "gerer_familles";

// Plafond du texte de page envoyé ; doit rester <= ChatbotController::MAX_PAGE_TEXT côté backend
const MAX_PAGE_TEXT = 150000;

/**
 * Écritures possibles dans la mémoire de l'assistant, renvoyées par le backend avec la réponse
 * (mêmes valeurs que ChatbotMemoireGestion::EVENEMENT_*), et clé de traduction de la mention affichée.
 */
export const MEMORY_EVENT_KEYS = {
    memorisee: "chatbotIaMemorySaved",
    remplacee: "chatbotIaMemoryReplaced",
    oubliee: "chatbotIaMemoryForgotten",
};

/** Pièces jointes au plus par conversation (même limite que ChatbotController::MAX_PIECES). */
export const MAX_PIECES = 5;

/** Types acceptés par l'API Claude : PDF (bloc document) et images (même liste que ChatbotGestion::TYPES_PIECES). */
export const TYPES_PIECES = ["application/pdf", "image/jpeg", "image/png", "image/gif", "image/webp"];

// Taille maximale d'une pièce une fois préparée (≈ 5 Mo) : son encodage base64 doit rester sous
// ChatbotController::MAX_PIECE_BASE64 (6,9 millions de caractères)
const MAX_PIECE_BYTES = 5000000;

// Plus grand côté d'une photo envoyée : au-delà, l'API la réduirait elle-même, autant ne pas
// transférer des Mo inutiles (photo de téléphone). 2000 px gardent lisible un tableau de bon de commande.
const MAX_IMAGE_SIDE = 2000;

// Une image déjà légère et à la bonne taille part telle quelle, sans recompression
const MAX_IMAGE_BYTES_AS_IS = 3500000;

/**
 * Lit un fichier en data URL (« data:<type>;base64,<données> »).
 *
 * @param {Blob} file
 * @returns {Promise<string>}
 */
const lireDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});

/**
 * Charge une image dans un élément <img> pour en connaître les dimensions et pouvoir la redessiner.
 * Échoue si le navigateur ne sait pas la décoder (HEIC hors Safari, par exemple).
 *
 * @param {string} dataUrl
 * @returns {Promise<HTMLImageElement>}
 */
const chargerImage = (dataUrl) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
});

/**
 * Prépare un fichier choisi par l'utilisateur pour l'envoi au chatbot.
 * - PDF : envoyé tel quel, jusqu'à MAX_PIECE_BYTES ;
 * - image : envoyée telle quelle si elle est d'un type accepté, légère et à la bonne taille ; sinon
 *   redessinée en JPEG, son plus grand côté ramené à MAX_IMAGE_SIDE (couvre aussi la photo HEIC d'un
 *   iPhone, que Safari sait décoder).
 * Le fichier ne quitte jamais le navigateur autrement que vers le backend, qui le transmet à Claude
 * sans l'enregistrer.
 *
 * @param {File} file - Fichier choisi dans le sélecteur
 * @returns {Promise<{nom: string, type: string, data: string}|{erreur: string}>} data en base64 sans
 *          préfixe, ou une clé de traduction d'erreur
 */
export const preparerPiece = async (file) => {
    const nom = (file.name || "document").slice(0, 200);
    try {
        if (file.type === "application/pdf") {
            if (file.size > MAX_PIECE_BYTES) return { erreur: "chatbotIaFileTooLarge" };
            const dataUrl = await lireDataUrl(file);
            return { nom, type: file.type, data: dataUrl.slice(dataUrl.indexOf(",") + 1) };
        }
        if (!file.type.startsWith("image/")) return { erreur: "chatbotIaFileUnsupported" };

        const dataUrl = await lireDataUrl(file);
        let img;
        try {
            img = await chargerImage(dataUrl);
        } catch (err) {
            return { erreur: "chatbotIaFileUnsupported" };
        }
        const plusGrandCote = Math.max(img.naturalWidth, img.naturalHeight);
        if (TYPES_PIECES.includes(file.type) && file.size <= MAX_IMAGE_BYTES_AS_IS && plusGrandCote <= MAX_IMAGE_SIDE) {
            return { nom, type: file.type, data: dataUrl.slice(dataUrl.indexOf(",") + 1) };
        }
        const echelle = Math.min(1, MAX_IMAGE_SIDE / plusGrandCote);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.naturalWidth * echelle);
        canvas.height = Math.round(img.naturalHeight * echelle);
        const ctx = canvas.getContext("2d");
        // Fond blanc : une image transparente (PNG) deviendrait noire en JPEG
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const jpeg = canvas.toDataURL("image/jpeg", 0.85);
        const data = jpeg.slice(jpeg.indexOf(",") + 1);
        if (data.length * 0.75 > MAX_PIECE_BYTES) return { erreur: "chatbotIaFileTooLarge" };
        return { nom, type: "image/jpeg", data };
    } catch (err) {
        return { erreur: "chatbotIaFileInvalid" };
    }
};

/**
 * Construit une réponse d'erreur affichable dans le chat.
 *
 * @param {string} key - Clé de traduction du message d'erreur
 * @returns {{blocks: Array, error: boolean}}
 */
const errorResponse = (key) => ({ blocks: [{ type: "text", key }], error: true });

/**
 * Appelle un endpoint du proxy chatbot et convertit la réponse (ou l'erreur) au format du chat.
 *
 * @param {string} action - Action du proxy (message | tool-result)
 * @param {Object} body - Corps de la requête
 * @returns {Promise<{blocks: Array, error?: boolean}|{pending: Object}|null>} null si la session a expiré
 */
const callChatbot = async (action, body) => {
    try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}api/chatbot/${action}`, body);
        if (!response.data.valid) {
            // Erreur de validation : le backend renvoie une clé de traduction
            const firstError = Object.values(response.data.errors || {})[0];
            return errorResponse((firstError && firstError[0]) || "unknowError");
        }
        const { text, key, pending, sources, validation, fichiers, memoire, usage } = response.data.data;
        if (pending) {
            // Seuls ces outils sont traités par le navigateur.
            // Un outil inconnu signifie presque toujours que cet onglet tourne sur une version du front
            // antérieure au backend (déploiement, ou page ouverte avant une mise à jour) : on le dit,
            // plutôt que d'afficher une erreur inexplicable comme « une erreur inattendue s'est produite ».
            return [TOOL_READ_PAGE, TOOL_HIGHLIGHT, TOOL_ASK_WEB, TOOL_ARTICLE_UPDATE, TOOL_ARTICLE_EXPORT, TOOL_FAMILLE_WRITE, TOOL_GENERATE_CHART].includes(pending.tool)
                ? { pending }
                : errorResponse("chatbotIaOutdated");
        }
        const blocks = [text ? { type: "text", text } : { type: "text", key: key || "chatbotIaNoAnswer" }];
        // Uniquement des adresses web : protège contre un lien javascript: ou autre schéma
        const webSources = (sources || []).filter((s) => typeof s.url === "string" && /^https?:\/\//i.test(s.url));
        if (webSources.length) blocks.push({ type: "sources", items: webSources });
        // Écriture refusée : le backend transporte les clés, les libellés sont ici (translations.js),
        // exactement ceux qu'affiche l'écran de l'article pour la même erreur
        const validationItems = (Array.isArray(validation) ? validation : []).map((item) => ({
            article: typeof item.article === "string" ? item.article : "",
            champ: typeof item.champ === "string" ? item.champ : "",
            keys: (Array.isArray(item.keys) ? item.keys : []).filter((k) => typeof k === "string"),
        })).filter((item) => item.keys.length);
        if (validationItems.length) blocks.push({ type: "validation", items: validationItems });
        // Fichiers produits par un outil (CSV d'import d'articles) : proposés au téléchargement sous la réponse
        (Array.isArray(fichiers) ? fichiers : [])
            .filter((f) => f && typeof f.nom === "string" && typeof f.contenu === "string")
            .forEach((f) => blocks.push({ type: "file", nom: f.nom, contenu: f.contenu, lignes: Number(f.lignes) || 0 }));
        // Mémoire : ce que le serveur a réellement enregistré ou supprimé, affiché sous la réponse pour
        // que l'utilisateur ne dépende pas de la seule phrase de l'assistant
        (Array.isArray(memoire) ? memoire : [])
            .filter((m) => m && MEMORY_EVENT_KEYS[m.action] && typeof m.texte === "string")
            .forEach((m) => blocks.push({ type: "memory", action: m.action, texte: m.texte }));
        // usage = jauge recalculée par le backend après la réponse (absente d'une réponse intermédiaire)
        return { blocks, usage };
    } catch (error) {
        if (error === "end_session") return null;
        // 403 = option chatbotIa inactive, 429 = trop de requêtes, 502 = API Claude indisponible
        const apiError = error.response && error.response.data && error.response.data.errors && error.response.data.errors.api;
        if (apiError) return errorResponse(apiError[0]);
        if (error.response && error.response.status === 429) return errorResponse("chatbotIaBusy");
        if (error.response && error.response.status === 403) return errorResponse("chatbotIaUnavailable");
        return errorResponse("unknowError");
    }
};

/**
 * Envoie la conversation au backend et renvoie la réponse de l'assistant.
 *
 * @param {Array<{role: string, content: string}>} messages - Historique, le dernier message étant celui de l'utilisateur
 * @param {string} page - Route courante de l'utilisateur (contexte pour le bot)
 * @param {string} lang - Langue de l'utilisateur dans Freebird (en/fr/es) : le chatbot répond dans cette
 *        langue pour toute la conversation (relue depuis le jeton d'attente aux messages suivants,
 *        inutile de la renvoyer à sendChatbotToolResult)
 * @param {Array<{message: number, nom: string, type: string, data: string}>} fichiers - Pièces jointes de la
 *        conversation, chacune avec l'index (dans `messages`) du message qui la porte. Le serveur ne stocke
 *        rien : elles sont renvoyées à chaque requête tant qu'elles restent dans l'historique.
 * @returns {Promise<{blocks: Array, error?: boolean}|{pending: Object}|null>} null si la session a expiré (redirection déjà lancée)
 */
export const sendChatbotMessage = (messages, page, lang, fichiers = []) =>
    callChatbot("message", { messages, page, lang, ...(fichiers.length ? { fichiers } : {}) });

/**
 * Renvoie au backend le résultat d'un outil traité par le navigateur et reprend la conversation.
 *
 * @param {string} pendingId - Jeton de la demande en attente (pending.id)
 * @param {{content?: string, approved?: boolean}} result - Texte de la page (lire_page) ou décision de l'utilisateur
 *        (demander_recherche_web, modifier_article)
 * @param {Array} fichiers - Mêmes pièces jointes que pour le message qui a ouvert la demande : le
 *        backend les retire du jeton d'attente (taille) et les remet en place à la reprise
 * @returns {Promise<{blocks: Array, error?: boolean}|{pending: Object}|null>}
 */
export const sendChatbotToolResult = (pendingId, result, fichiers = []) =>
    callChatbot("tool-result", { pending_id: pendingId, ...result, ...(fichiers.length ? { fichiers } : {}) });

/**
 * Propose au téléchargement un fichier produit par l'assistant (CSV d'import d'articles). Le contenu
 * vient tel quel du serveur, BOM UTF-8 compris (Excel lit alors correctement les accents).
 *
 * @param {string} nom - Nom du fichier
 * @param {string} contenu - Contenu texte du fichier
 */
export const telechargerFichier = (nom, contenu) => {
    const url = URL.createObjectURL(new Blob([contenu], { type: "text/csv;charset=utf-8" }));
    const lien = document.createElement("a");
    lien.href = url;
    lien.download = nom;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    // Laisse au navigateur le temps de démarrer le téléchargement avant de libérer l'objet
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Lit le texte visible de la page ouverte : zone principale du layout et fenêtres modales ouvertes.
 * Menus, barre latérale et chat sont exclus. innerText ignore les éléments masqués (display: none)
 * et les valeurs saisies dans les champs de formulaire (choix de l'utilisateur).
 *
 * @returns {string} Texte de la page, tronqué au-delà de MAX_PAGE_TEXT caractères
 */
export const readCurrentPage = () => {
    const parts = [];
    const main = document.querySelector(".main-content");
    if (main) parts.push(main.innerText);
    document.querySelectorAll(".modal.show").forEach((modal) => parts.push(modal.innerText));

    // Texte destiné à Claude uniquement (jamais affiché) : les séparateurs ne passent pas par la traduction
    let text = parts
        .map((part) => part.replace(/\n{3,}/g, "\n\n").trim())
        .filter(Boolean)
        .join("\n\n--- Fenêtre ouverte ---\n\n");
    if (text.length > MAX_PAGE_TEXT) {
        text = text.slice(0, MAX_PAGE_TEXT) + "\n\n[Contenu tronqué : la page est trop longue pour être lue en entier]";
    }
    return text;
};

/**
 * Normalise un texte pour la comparaison : espaces multiples réduits, minuscules.
 *
 * @param {string} text
 * @returns {string}
 */
const normalizeText = (text) => (text || "").replace(/\s+/g, " ").trim().toLowerCase();

/** Retire l'entourage posé par l'assistant (appelé avant un nouveau surlignage ou un nouveau message). */
export const clearHighlight = () => {
    document.querySelectorAll("." + HIGHLIGHT_CLASS).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
};

/**
 * Entoure en rouge l'élément visible le plus précis qui contient le texte demandé, et fait défiler jusqu'à lui.
 * Recherche limitée aux zones lues par readCurrentPage (zone principale et fenêtres ouvertes).
 *
 * @param {string} target - Texte à retrouver, copié par Claude depuis la page
 * @returns {string} Compte rendu renvoyé à Claude (trouvé ou non)
 */
export const highlightOnPage = (target) => {
    clearHighlight();
    const wanted = normalizeText(target);
    if (!wanted) return "Aucun texte indiqué : rien n'a été entouré.";

    const roots = [document.querySelector(".main-content"), ...document.querySelectorAll(".modal.show")].filter(Boolean);
    // textContent (rapide) pour filtrer, puis on garde les éléments réellement affichés
    const candidates = roots
        .flatMap((root) => Array.from(root.querySelectorAll("*")))
        .filter((el) => normalizeText(el.textContent).includes(wanted) && el.getClientRects().length > 0);
    if (!candidates.length) return `Texte « ${target} » introuvable sur la page : rien n'a été entouré.`;

    // Élément le plus précis : aucun de ses enfants ne contient aussi le texte
    const precise = candidates.filter((el) => !candidates.some((other) => other !== el && el.contains(other)));
    const element = precise[0];
    element.classList.add(HIGHLIGHT_CLASS);
    element.scrollIntoView({ behavior: "smooth", block: "center" });

    const others = precise.length > 1 ? ` (${precise.length - 1} autre(s) occurrence(s) non entourée(s))` : "";
    return `Élément contenant « ${target} » entouré en rouge et affiché à l'écran${others}.`;
};

/**
 * Charge la jauge de consommation du mois (pack IA de la société).
 * Le backend ne renvoie que des pourcentages : { pack, scope: 'global'|'personal', percent, marque,
 * blocage, peut_debloquer, devise }. 'marque' est la position de la fin du pack quand un dépassement
 * est autorisé (jauge de la société uniquement).
 *
 * @returns {Promise<Object|null>} null si la jauge n'a pas pu être chargée
 */
export const fetchChatbotUsage = async () => {
    try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}api/chatbot/usage`);
        return response.data.valid ? response.data.data : null;
    } catch (error) {
        return null;
    }
};

/**
 * Débloque un dépassement supplémentaire pour le mois en cours (utilisateurs autorisés par Freebird).
 *
 * @param {number} amount - Montant en prix de vente, dans la devise du pack
 * @returns {Promise<{usage: Object, suivi: Object|null}|{erreur: string}>} Jauge (et suivi) à jour, ou clé d'erreur traduite
 */
export const unlockChatbotOverage = (amount) => callQuota("post", "unlock", { amount });

/**
 * Règle le plafond de dépassement mensuel de la société (utilisateurs autorisés par Freebird).
 *
 * @param {number|null} cap - Montant en prix de vente, dans la devise du pack ; null = aucun plafond
 * @returns {Promise<{usage: Object, suivi: Object|null}|{erreur: string}>}
 */
export const saveChatbotOverageCap = (cap) => callQuota("put", "overage", { cap });

/**
 * Charge le suivi du mois pour le module de configuration : situation de la société et consommation
 * de chaque utilisateur (pourcentages, et montants en prix de vente pour le dépassement).
 *
 * @returns {Promise<Object|{erreur: string}>}
 */
export const fetchChatbotQuotas = () => callQuota("get", "quotas");

/**
 * Règle la limite d'un utilisateur, en % de ce qui est autorisé à la société pour le mois.
 *
 * @param {number} userId - Utilisateur de la société
 * @param {number|null} percent - Entre 0 et 100 ; null = sans limite
 * @returns {Promise<Object|{erreur: string}>} Suivi à jour, ou clé d'erreur traduite
 */
export const saveChatbotQuota = (userId, percent) => callQuota("put", "quota", { user_id: userId, percent });

/**
 * Autorise ou interdit l'assistant IA à un utilisateur (même restriction que l'onglet Général de la
 * configuration des accès). Interdit, il ne voit plus la bulle à son prochain chargement.
 *
 * @param {number} userId - Utilisateur de la société
 * @param {boolean} allowed - true = autorisé
 * @returns {Promise<Object|{erreur: string}>} Suivi à jour, ou clé d'erreur traduite
 */
export const saveChatbotAccess = (userId, allowed) => callQuota("put", "access", { user_id: userId, allowed });

/**
 * Charge la mémoire de l'assistant : les consignes que l'utilisateur connecté lui a demandé de retenir
 * (« souviens-toi que... »). Elles sont personnelles : le backend ne renvoie jamais celles d'un autre.
 *
 * @returns {Promise<{consignes: Array<{id: number, content: string}>, max: number}|{erreur: string}>}
 */
export const fetchChatbotConsignes = () => callQuota("get", "consignes");

/**
 * Supprime une consigne de la mémoire de l'assistant.
 *
 * @param {number} id - Consigne de l'utilisateur connecté
 * @returns {Promise<{consignes: Array, max: number}|{erreur: string}>} Mémoire à jour, ou clé d'erreur traduite
 */
export const deleteChatbotConsigne = (id) => callQuota("delete", `consignes/${id}`);

/**
 * Appelle un endpoint du chatbot qui ne passe pas par Claude (pack IA, mémoire) et renvoie ses
 * données, ou une clé d'erreur traduite dans 'erreur'.
 *
 * @param {string} method - get | put | post | delete
 * @param {string} action - usage | quotas | quota | overage | unlock | consignes | consignes/{id}
 * @param {Object} [body] - Corps de la requête
 */
const callQuota = async (method, action, body) => {
    try {
        const response = await axios[method](`${process.env.REACT_APP_API_URL}api/chatbot/${action}`, body);
        if (!response.data.valid) {
            const firstError = Object.values(response.data.errors || {})[0];
            return { erreur: (firstError && firstError[0]) || "unknowError" };
        }
        return response.data.data;
    } catch (error) {
        if (error === "end_session") return { erreur: "" };
        return { erreur: error.response && error.response.status === 403 ? "forbiddenGeneral" : "unknowError" };
    }
};
